import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * Staff-only card-trial dashboard: every Stripe subscription tagged as a card
 * trial, the PostHog signup funnel since go-live (total and per Toronto day),
 * and each trial workspace's in-app activity. The server reads Stripe and
 * PostHog and caches the result; the portal scores and draws it
 * (containers/management/card-trials). 403 unless the caller is staff.
 *
 * Types mirror the card-trials response in cleanse_api (snake_case of the
 * original dashboard's `toTrial` rows and PostHog tables). Each source fails
 * on its own: a null section comes with its `*_error` text.
 */

/** One funnel row: distinct people per step. */
export interface CardTrialFunnel {
  signed_up: number;
  saw: number;
  clicked: number;
  skip_popup: number;
  kept_trial: number;
  chose_free: number;
}

/** A trial workspace's in-app activity since go-live. `*_ts` are unix
 *  seconds, 0 when the event never happened. */
export interface CardTrialActivity {
  enriched: number;
  exports: number;
  lists: number;
  smart_cols: number;
  agent_started: number;
  agent_done: number;
  chats: number;
  extension: number;
  crm: number;
  plan_clicks: number;
  upgrades: number;
  topups: number;
  cancel_flow: number;
  out_of_credits: number;
  out_ts: number;
  dismissed: number;
  call_booked: number;
  book_clicks: number;
  /** Distinct Toronto days with any event. */
  days: number;
  last_ts: number;
}

/** active: trialing, renews · cancel: cancelled (or will) · paid: converted ·
 *  up: converted to a bigger plan (pre-v4 pricing) · fail: renewal failed. */
export type CardTrialKind = 'active' | 'cancel' | 'paid' | 'up' | 'fail';

/** The card on file: the subscription's own, else the customer's default. */
export interface CardTrialPaymentMethod {
  type: string;
  brand: string;
  last4: string;
  /** credit | debit | prepaid | '' */
  funding: string;
  country: string;
  fingerprint: string;
  /** apple_pay, google_pay, … or '' */
  wallet: string;
  cvc_fail: boolean;
  exp_month: number;
  exp_year: number;
}

export interface CardTrialRow {
  /** Stripe subscription id. */
  id: string;
  kind: CardTrialKind;
  /** Stripe subscription status. */
  status: string;
  cancelling: boolean;
  name: string;
  email: string;
  /** Billing country (ISO 3166-1 alpha-2) or ''. */
  country: string;
  /** Trial start / end as Toronto days, YYYY-MM-DD. */
  start: string;
  end: string;
  start_ms: number;
  end_ms: number;
  tier: string;
  plan: string;
  /** Renewal amount in USD. */
  amount: number;
  interval: string;
  /** Monthly recurring revenue in USD. */
  mrr: number;
  /** Pricing v4 (plan picked at checkout). */
  v4: boolean;
  seats: number;
  /** v4 checkout added a credit pack. */
  addon: boolean;
  /** Cleanlist organization id, '' when the subscription carries none. */
  org: string;
  pm: CardTrialPaymentMethod | null;
}

export interface CardTrialsReport {
  generated_at: string;
  /** YYYY-MM-DD, Toronto. */
  go_live_day: string;
  timezone: string;
  /** Stripe's settled USD→CAD rate; null shows USD. */
  fx: number | null;
  /** null: Stripe failed (see stripe_error). */
  trials: CardTrialRow[] | null;
  /** null: PostHog failed (see posthog_error). */
  funnel_total: CardTrialFunnel | null;
  /** Keyed by Toronto day. */
  funnel_daily: Record<string, CardTrialFunnel>;
  /** Keyed by organization id. null: not loaded or failed (activity_error). */
  activity: Record<string, CardTrialActivity> | null;
  /** organization id → Toronto day → event count. */
  activity_days: Record<string, Record<string, number>>;
  stripe_error: string | null;
  posthog_error: string | null;
  activity_error: string | null;
}

export interface CardTrialsArgs {
  /** true makes the server skip its cache and read Stripe and PostHog. */
  refresh?: boolean;
}

export const adminCardTrialsApi = createApi({
  reducerPath: 'adminCardTrialsApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getCardTrials: builder.query<CardTrialsReport, CardTrialsArgs | void>({
      query: (arg) => ({
        url: '/management/card-trials',
        params: { refresh: arg?.refresh ? 'true' : 'false' },
      }),
      // One cache entry whatever `refresh` says, so a forced refresh (the
      // lazy hook's trigger) replaces the data every subscriber shows.
      serializeQueryArgs: ({ endpointName }) => endpointName,
    }),
  }),
});

export const { useGetCardTrialsQuery, useLazyGetCardTrialsQuery } =
  adminCardTrialsApi;
